import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { DetailViewFeatureParams } from 'src/app/component/detail-view/detail-view.model';
import { ConfigService } from 'src/app/config/config.service';
import { ArbeitsbereichService } from 'src/app/shared/service/arbeitsbereich.service';
import { LayerContainerService } from 'src/app/shared/service/layer-container.service';
import { LayerQueryService } from 'src/app/shared/service/layer-query.service';
import { WorkspaceGuard } from '../../../core/guards/workspace.guard';
import { ToolGuard } from '../../../core/guards/tool.guard';
import { WorkspaceNotifyService } from '../../../component/header-panel/arbeitsbereich/workspace-notify.service';
import Enums from '../../../shared/enum/enums';
import { DetailViewForm } from '../../../config/model/detailview-config.model';
import { GlobalState } from '../../../config/model/global-state.model';

@Injectable({
  providedIn: 'root',
})
export class FeatureParamsResolverService {
  private _routeSnapshot: ActivatedRouteSnapshot;
  private routeState: RouterStateSnapshot;
  private changedWorkspace = false;

  constructor(
    private layerQueryService: LayerQueryService,
    private configService: ConfigService,
    private layerContainerService: LayerContainerService,
    private arbeitsbereichService: ArbeitsbereichService,
    private workspaceNotifyService: WorkspaceNotifyService,
    private workspacheGuard: WorkspaceGuard,
    private toolGuard: ToolGuard
  ) {}

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<DetailViewFeatureParams> {
    this._routeSnapshot = route;
    this.routeState = state;

    const id = route.queryParams.objectid;
    const guid = route.queryParams.guid;
    const aoid = route.queryParams.aoid;
    const egrid = route.queryParams.egrid;

    const allEditableLyr = this.getAllEditableLayers();
    if (!allEditableLyr) return of(null);

    const layerUrl = this.configService.getLayerUrl(this._routeSnapshot.paramMap.get('layer'));
    const nonGuid = this.hasLayerGuidAttribute(this._routeSnapshot.paramMap.get('layer'));

    if(nonGuid) {
      return this.getFeatureNonGuid(id, layerUrl, egrid).pipe(map(resp =>{
        return resp;
      }))
    }

    return this.getGlobalId(id, layerUrl, guid, aoid).pipe(
      mergeMap(globalId => {
        if (!globalId) {
          this.handleError('Global ID is required to fetch the feature');
          return of(null);
        }
        return this.getFeature(globalId, layerUrl).pipe(
          tap(() => {
            // console.log('Feature resolved successfully');
          }),
          catchError(error => {
           this.handleError('Error getting feature: ', error);
            return of(null);
          })
        );
      }),
      catchError(error => {
        this.handleError('Error in resolve method: ', error);
        return of(null);
      })
    );
  }

  /**
   * Gets a feature from a specific layer by globalId.
   */
  private getFeature(globalId: string, layerUrl: string): Observable<DetailViewFeatureParams> {
    return this.layerQueryService
      .query(layerUrl, `GLOBALID='${globalId}'`, ['*'], ['globalId'], true)
      .pipe(
        map(response => {
          if (!response.features || response.features.length === 0) {
            console.error('No feature found for the given GlobalID');
            return;
          }
          return this.createFeatureParams(response);
        })
      );
  }

  /**
   * Gets a feature from a specific layer by globalId.
   */
  private getFeatureNonGuid(id: number, layerUrl: string, egrid: string): Observable<DetailViewFeatureParams> {
    const where = id ? `OBJECTID=${id}`: egrid ? `EGRIS_EGRID='${egrid}'` : null;
    return this.layerQueryService
      .query(layerUrl, where, ['*'], ['objectid'], true)
      .pipe(
        map(response => {
          if (!response.features || response.features.length === 0) {
            console.error('No feature found for the given objectid');
            return;
          }
          return this.createFeatureParams(response);
        })
      );
  }

  /**
   * Gets globalId via given number from a specific layer.
   */
  private getGlobalId(
    objectId: number,
    layerUrl: string,
    guid: string,
    aoid: number
  ): Observable<string> {
    if (guid) {
      return of(guid);
    }
    const where = objectId ? `OBJECTID=${objectId}` : aoid ? `AO_ID_GR=${aoid}` : null;

    return this.layerQueryService.query(layerUrl, where, ['GLOBALID']).pipe(
      map((response: __esri.FeatureSet) => {
        if (!response.features || response.features.length === 0) {
          this.handleError('No feature found for the given Objectnumber!');
          return;
        }
        return response.features[0].attributes['GLOBALID'];
      })
    );
  }

  /**
   * creates feature-params as detailview needs it.
   */
  private createFeatureParams(response: __esri.FeatureSet): DetailViewFeatureParams {
    const featureLayer = this.getFeatureLayer();
    const detailviewForms = this.getDetailViewConfig();

    const fkIdentifyLayerId = detailviewForms.forms.find(f => f.editableLayerId === featureLayer.id)
      .fkIdentifyLayerId;

    const featureParams: DetailViewFeatureParams = {
      currentArbeitsBereichLyrs: [{ esriLayer: featureLayer }],
      feature: response.features[0],
      layerId: fkIdentifyLayerId,
      mapServerId: detailviewForms.mapServerId,
      layerName: featureLayer.id,
      createMode: false,
      workspace: detailviewForms.fkArbeitsbereich as Enums.Workspace,
      fkIdentifyLayer: detailviewForms.fkIdentifyLayer,
      featureLayerUrl: this.getFkIdentifyLayerUrl(),
    };

    featureParams.changedWorkspace = this.changedWorkspace;

    return featureParams;
  }

  /**
   * Returns all editable layers from current arbeitsbereich.
   */
  private getAllEditableLayers(): __esri.FeatureLayer[] {
    const editableLayers: __esri.FeatureLayer[] = [];
    if (!this.arbeitsbereichService.selectedArbeitsbereich) {
      this.selectArbeitsbereich();
    } else {
      this.changedWorkspace = false;
    }

    if (!this.arbeitsbereichService.selectedArbeitsbereich.editableLayers) return null;

    this.arbeitsbereichService.selectedArbeitsbereich.editableLayers.forEach(editableLayer => {
      const fkLayer = editableLayer.fkLayer;
      const layers = this.layerContainerService.layerList.filter(
        f => f.esriLayer.id === fkLayer && f.esriLayer.type === 'feature'
      );
      if (layers.length === 1) editableLayers.push(layers[0].esriLayer as __esri.FeatureLayer);
    });

    return editableLayers;
  }

  private getFeatureLayer(): __esri.FeatureLayer {
    const layers = this.layerContainerService.layerList.filter(
      f =>
        f.esriLayer.id === this._routeSnapshot.paramMap.get('layer').toLowerCase() &&
        f.esriLayer.type === 'feature'
    );
    return layers[0].esriLayer as __esri.FeatureLayer;
  }

  private getDetailViewConfig(): DetailViewForm {
    return this.configService.detailViewConfig.detailviews.find(dv =>
      dv.forms.find(f => f.editableLayerId === this._routeSnapshot.paramMap.get('layer'))
    );
  }

  private getFkIdentifyLayerUrl(): string {
    const featureLayer = this.getFeatureLayer();
    const detailViewConfig = this.getDetailViewConfig();
    const fkIdentifyLayerId = detailViewConfig.forms.find(
      f => f.editableLayerId === featureLayer.id
    ).fkIdentifyLayerId;

    const mapLayr = this.layerContainerService.layerList.find(
      f => f.esriLayer.id === detailViewConfig.fkIdentifyLayer
    );

    return `${mapLayr.layerUrl}/${fkIdentifyLayerId}`;
  }

  /**
   * Selects the default Arbeitsbereich if none is selected.
   */
  private selectArbeitsbereich() {
    const ws = this.workspacheGuard.workSpace;
    this.arbeitsbereichService.setSelected(
      this.configService.arbeitsbereichConfig.arbeitsbereiche.filter(f => f.id === ws)[0],
      true,
      this.createStateOptions()
    );
    this.changedWorkspace = true;
    this.workspaceNotifyService.setWorkspace(this.arbeitsbereichService.selectedArbeitsbereich);
  }

  private createStateOptions(): Partial<GlobalState> {
    const ws = this.workspacheGuard.workSpace;
    const toolCode: Enums.ToolCode = this.toolGuard.tool;
    return {
      workspace: ws,
      tool: toolCode,
      layer: this._routeSnapshot.paramMap.get('layer'),
      id: Number(this._routeSnapshot.queryParams.objectid),
      guid: this._routeSnapshot.queryParams.guid,
      aoid: this._routeSnapshot.queryParams.aoid,
      egrid: this._routeSnapshot.queryParams.egrid
    };
  }

  private handleError<T>(message: string, error?: any): Observable<T> {
    console.error(message, error);
    return of(null as T);
  }

  private hasLayerGuidAttribute(layerId: string): boolean {
    const detailViewConfig = this.configService.detailViewConfig.detailviews.find(dv =>
      dv.forms.find(f => f.editableLayerId === layerId)
    );
    return detailViewConfig.nonGuid;
  }
}
